'use client'
import { toast } from '@/components/common/toast'
import { useForgotPasswordMutation } from '@/hooks/react-query/auth'
import { validateEmail, validatePhone } from '@/lib/utils/validation'
import { hidePopup, showPopup } from '@/providers/popup-provider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { OtpForm } from './otp-form'
import { ResetPassword } from './reset-password'

const formSchema = z.object({
  address: z
    .string()
    .min(1, 'Vui lòng nhập Email/SĐT của bạn')
    .refine(
      (value) => {
        const isEmail = validateEmail(value)
        const phoneValidate = validatePhone(value)
        return isEmail || phoneValidate.valid
      },
      (value) => {
        if (value.includes('@')) {
          return {
            message: 'Email/SĐT không hợp lệ',
          }
        }

        if (typeof +value === 'number' && !isNaN(+value)) {
          return {
            message: 'Số điện thoại không hợp lệ',
          }
        }

        return {
          message: 'Vui lòng nhập Email/SĐT hợp lệ',
        }
      }
    ),
})

export const ForgotPasswordForm = () => {
  const [error, setError] = useState('')

  const { mutateAsync: forgotPassword, isPending } = useForgotPasswordMutation()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: '',
    },
  })

  const handleShowResetPassword = async (forgotCode: string) => {
    showPopup(
      <ResetPassword
        forgotCode={forgotCode}
        onClose={() => hidePopup()}
        onSuccess={() => {
          toast.message({
            title: 'Thành công',
            message: 'Đặt lại mật khẩu thành công',
          })
        }}
      />
    )
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setError('')
      const isEmail = validateEmail(values.address)

      const forgotPasswordBody = {
        type: isEmail ? 'email' : 'phone',
        value: values.address,
      } as const

      const res = await forgotPassword(forgotPasswordBody)

      if (!res.state) {
        setError(res.message)
        return
      }

      // case phone
      if (isEmail) {
        // TODO: implement email
        toast.message({
          title: 'Thành công',
          message: 'Mã OTP đã được gửi thành công',
        })
        hidePopup()
      } else {
        setTimeout(() => {
          showPopup(
            <OtpForm
              phone={values.address}
              onSuccess={handleShowResetPassword}
              type='forgot_password'
              isAuth={true}
              onClose={() => hidePopup()}
            />
          )
        }, 100)
      }

      toast.message({
        title: 'Thành công',
        message: 'Mã OTP đã được gửi thành công',
      })
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      } else if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error
      ) {
        setError((error as { message: string }).message)
      } else {
        setError('Gửi yêu cầu thất bại, vui lòng thử lại')
      }
    }
  }

  return (
    <>
      <h3 className='font-criteriacapitalize text-heading-5 mb-4 text-center font-medium tracking-normal text-neutral-900'>
        Cấp lại mật khẩu
      </h3>

      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <Input
          label='Email/SĐT của bạn'
          className='w-full'
          floating
          autoFocus
          error={form.formState.errors?.address}
          {...form.register('address')}
        />
        {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}

        <Button
          className='mt-6 w-full normal-case'
          variant='black'
          loading={isPending}
          disabled={!form.formState.isValid}
        >
          Gửi yêu cầu
        </Button>
      </form>
    </>
  )
}
